/**
 * o16-card-carousel.js
 * Mobile-only swipeable mini-carousel for product card media (PLP).
 *
 * Targets: .media.media--hover-effect that contain two direct <img> children —
 * the CSS hover-swap path (settings.on_hover == 'hover'). The Swiper slider path
 * (.swiper.product-card-js) is already handled by product-card-slider.js and is
 * skipped here.
 *
 * Desktop (≥750px): complete no-op. CSS hover-swap in component-card.css is untouched.
 * Mobile (<750px):  wraps the two images into Swiper slides, adds a subtle
 *                   "Swipe →" hint (fades out after the first real swipe),
 *                   initialised lazily via IntersectionObserver.
 *
 * Stacking + tap behaviour: the card-wide overlay <a> (.card-wrapper__link--overlay)
 * is position:absolute over the whole card and would otherwise sit above this
 * media element (both resolve to z-index:1 — Swiper's own bundled CSS sets that
 * on .swiper — so the later-in-DOM overlay wins the tie and swallows every touch).
 * o16-card-carousel.css raises the active swiper's z-index above the overlay so
 * Swiper actually receives the drag gesture. Because the overlay is a DOM
 * *sibling* of this media element (not an ancestor), a native click on a slide
 * never bubbles into it, so clean-tap navigation is wired explicitly below via
 * Swiper's own 'tap' event — which only fires when the touch stayed under the
 * drag threshold (preventClicks:true additionally suppresses the native click
 * after a real drag).
 *
 * Requires: Swiper 11 (loaded via swiper-bundle.min.js, global `Swiper` constructor).
 */

(function () {
  'use strict';

  var MOBILE_MQ = '(max-width: 749px)';

  // ─── DOM helpers ────────────────────────────────────────────────────────────

  /**
   * Build the Swiper DOM structure inside a .media.media--hover-effect element.
   *
   * Before:
   *   .media.media--hover-effect
   *     img.media--first   (primary image, lazy-loaded)
   *     img.media--second  (secondary image, lazy-loaded)
   *
   * After:
   *   .media.media--hover-effect.o16-card-carousel--active
   *     .o16-card-swiper.swiper
   *       .swiper-wrapper
   *         .swiper-slide > img (primary)
   *         .swiper-slide > img (secondary)
   *     .o16-card-swipe-hint  ("Swipe →" — matches icon-arrow.liquid's thin
   *                            arrow motif; fades out after the first swipe)
   *
   * Navigation to the product page is handled by the card-wrapper overlay <a>
   * that sits outside and above this container, so no link cloning is needed.
   */
  function buildCarousel(mediaEl) {
    var imgs = Array.prototype.slice.call(
      mediaEl.querySelectorAll(':scope > img')
    );
    if (imgs.length < 2) return; // single image — nothing to do

    // Build swiper-wrapper with one slide per image
    var swiperWrapper = document.createElement('div');
    swiperWrapper.className = 'swiper-wrapper';

    imgs.forEach(function (img) {
      var slide = document.createElement('div');
      slide.className = 'swiper-slide';
      slide.appendChild(img); // move existing img node into the slide
      swiperWrapper.appendChild(slide);
    });

    // Swiper root
    var swiperEl = document.createElement('div');
    swiperEl.className = 'o16-card-swiper swiper';
    swiperEl.appendChild(swiperWrapper);

    // "Swipe →" hint — replaces pagination dots. Decorative only (aria-hidden);
    // the arrow glyph echoes the theme's existing thin-arrow motif (see
    // icon-arrow.liquid / the "→" cue used elsewhere). Sits as a sibling of
    // the swiper root, not inside it, so it survives outside the slide track.
    var hintEl = document.createElement('span');
    hintEl.className = 'o16-card-swipe-hint';
    hintEl.setAttribute('aria-hidden', 'true');
    // Non-breaking space keeps "Swipe" and "→" from wrapping onto two lines.
    hintEl.textContent = 'Swipe →';

    mediaEl.appendChild(swiperEl);
    mediaEl.appendChild(hintEl);
    mediaEl.classList.add('o16-card-carousel--active');
  }

  /**
   * Initialise a Swiper instance on a media element that has been built.
   */
  function initSwiper(mediaEl) {
    var swiperEl = mediaEl.querySelector('.o16-card-swiper');
    if (!swiperEl || swiperEl._o16SwiperInstance) return;

    var instance = new Swiper(swiperEl, {
      direction: 'horizontal',
      loop: false,
      // Touch tuning — swipe is recognised; clean tap still navigates the card
      allowTouchMove: true,
      threshold: 10,
      preventClicks: true,
      preventClicksPropagation: false,
      // No pagination module — the "Swipe →" hint (below) replaces the dots.
      // Minimal a11y
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous image',
        nextSlideMessage: 'Next image',
      },
    });

    swiperEl._o16SwiperInstance = instance;

    // ─── "Swipe →" hint: fade out after the first real swipe ─────────────────
    // It's a one-time hint, not a persistent control — once the shopper has
    // proven they know the card swipes (a genuine slide change, not a tap),
    // it fades via CSS (o16-card-swipe-hint--hidden). Swiper's 'slideChange'
    // only fires on an actual index change, so a clean tap-to-navigate (which
    // never changes the active slide) never triggers this.
    var hintEl = mediaEl.querySelector('.o16-card-swipe-hint');
    if (hintEl) {
      instance.once('slideChange', function () {
        hintEl.classList.add('o16-card-swipe-hint--hidden');
      });
    }

    // ─── Clean-tap navigation ────────────────────────────────────────────────
    // The card-wide overlay link (.card-wrapper__link--overlay) is a *sibling*
    // of this media element, not an ancestor — so a native click on a slide
    // never bubbles into it. o16-card-carousel.css raises this swiper above
    // that overlay (z-index) so touch/drag gestures reach Swiper at all; that
    // same z-index bump means the overlay no longer receives taps over the
    // media area either, so navigation for a clean tap is wired explicitly
    // here via Swiper's own 'tap' event — which Swiper only emits when the
    // touch did NOT exceed the drag threshold (i.e. allowClick is still true),
    // so a genuine swipe never triggers it.
    var productUrl = mediaEl.getAttribute('data-product-url');
    if (productUrl && productUrl !== '#') {
      instance.on('tap', function () {
        window.location.href = productUrl;
      });
    }
  }

  // ─── Candidate discovery ─────────────────────────────────────────────────────

  /**
   * Find all .media.media--hover-effect elements that qualify:
   * - Have two or more direct <img> children (the hover-swap path)
   * - Do NOT already contain a .product-card-js (Swiper slider mode)
   * - Have NOT yet been activated
   */
  function findCandidates(root) {
    var candidates = [];
    (root || document)
      .querySelectorAll('.media.media--hover-effect')
      .forEach(function (el) {
        if (el.classList.contains('o16-card-carousel--active')) return;
        if (el.querySelector('.product-card-js')) return;
        if (el.closest('.complementary-products')) return; // "Pairs well with" — its per-card swipe fights the outer recommendation slider
        var directImgs = el.querySelectorAll(':scope > img');
        if (directImgs.length >= 2) candidates.push(el);
      });
    return candidates;
  }

  // ─── Lazy initialisation via IntersectionObserver ────────────────────────────

  function observe(candidates) {
    if (!window.IntersectionObserver) {
      // Fallback: eager init for old browsers
      candidates.forEach(function (el) {
        buildCarousel(el);
        initSwiper(el);
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var mediaEl = entry.target;
          observer.unobserve(mediaEl);
          buildCarousel(mediaEl);
          initSwiper(mediaEl);
        });
      },
      {
        // Begin building the carousel when the card is 200px away from the viewport
        rootMargin: '200px 0px',
        threshold: 0,
      }
    );

    candidates.forEach(function (el) {
      observer.observe(el);
    });
  }

  // ─── Entry points ────────────────────────────────────────────────────────────

  function run() {
    if (!window.matchMedia(MOBILE_MQ).matches) return;
    if (typeof Swiper === 'undefined') return;
    var candidates = findCandidates(document);
    if (candidates.length) observe(candidates);
  }

  function onReady(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  onReady(function () {
    // swiper-bundle.min.js is deferred; a setTimeout(0) ensures it has executed
    // (deferred scripts run in order, and ours follows swiper in document order,
    // but the extra tick is a safe guard).
    setTimeout(run, 0);

    // Re-run after Shopify editor section reloads or AJAX-injected card grids
    document.addEventListener('shopify:section:load', function () {
      setTimeout(run, 0);
    });
  });
})();
